import React, { useState } from "react";
import { Box, styled, keyframes } from "@mui/material";
import { Header } from "./components/Header";
import { LeftPanel } from "./components/left-panel/LeftPanel";
import { CenterPanel } from "./components/center-panel/CenterPanel";
import { ActivityPanel } from "./components/right-panel/ActivityPanel";
import { BackLeftPanel } from "./components/left-panel/BackLeftPanel";
import { BackCenterPanel } from "./components/center-panel/BackCenterPanel";
import { BackRightPanel } from "./components/right-panel/BackRightPanel";
import { PerkQuirkActivity } from "./components/activities/perks-quirks/PerkQuirkActivity";
import { JobGearActivity } from "./components/activities/job-gear/JobGearActivity";
import { HighSchoolPrioritiesActivity } from "./components/activities/high-school-priorities/HighSchoolPrioritiesActivity";
import { AfterHighSchoolActivity } from "./components/activities/after-high-school/AfterHighSchoolActivity";
import type { MSHatDisplayProps, ActivityType } from "./types";

const DisplayContainer = styled(Box, {
  label: "MSHatDisplayContainer",
})(() => ({
  position: "relative",
  width: "100%",
  height: "100vh",
  backgroundColor: "#0d0d0d",
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
  overflowY: "auto",
}));

const BackgroundImage = styled(Box, {
  label: "MSHatDisplayBackground",
  shouldForwardProp: (prop) => prop !== "backgroundUrl",
})<{ backgroundUrl?: string }>(({ backgroundUrl }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: backgroundUrl ? `url('${backgroundUrl}')` : "none",
  backgroundSize: "cover",
  backgroundPosition: "center",
  opacity: 0.2,
  pointerEvents: "none",
}));

const MainContent = styled(Box, {
  label: "MSHatDisplayMainContent",
})(() => ({
  flex: 1,
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  position: "relative",
  zIndex: 1,
  paddingBottom: 16,
  minHeight: 601,
}));

const MainContainer = styled(Box, {
  label: "MSHatDisplayMainContainer",
})(() => ({
  display: "flex",
  gap: 32,
  height: "100%",
  alignItems: "center",
  minHeight: 569,
}));

const SidePanelWrapper = styled(Box, {
  label: "SidePanelWrapper",
})(() => ({
  position: "relative",
  width: 368,
  height: 569,
  flexShrink: 0,
}));

const SideFace = styled(Box, {
  label: "SideFace",
  shouldForwardProp: (prop) => prop !== "visible",
})<{ visible?: boolean }>(({ visible }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  opacity: visible ? 1 : 0,
  pointerEvents: visible ? "auto" : "none",
  transition: "opacity 0.84s ease-in-out",
}));

// Flip keyframes: anticipate slightly wrong-way, overshoot past target, settle
const flipForward = keyframes`
  0%   { transform: rotateY(0deg)   rotateX(0deg); }
  20%  { transform: rotateY(-25deg) rotateX(0deg); }
  50%  { transform: rotateY(90deg)  rotateX(8deg); }
  80%  { transform: rotateY(205deg) rotateX(0deg); }
  100% { transform: rotateY(180deg) rotateX(0deg); }
`;

const flipBackward = keyframes`
  0%   { transform: rotateY(180deg) rotateX(0deg); }
  20%  { transform: rotateY(205deg) rotateX(0deg); }
  50%  { transform: rotateY(90deg)  rotateX(8deg); }
  80%  { transform: rotateY(-25deg) rotateX(0deg); }
  100% { transform: rotateY(0deg)   rotateX(0deg); }
`;

type FlipState =
  | "idle-front"
  | "flipping-to-back"
  | "idle-back"
  | "flipping-to-front";

const CenterFlipWrapper = styled(Box, {
  label: "CenterFlipWrapper",
})(() => ({
  height: "100%",
  aspectRatio: "368 / 569",
  flexShrink: 0,
  perspective: "1200px",
}));

const CenterFlipper = styled(Box, {
  label: "CenterFlipper",
  shouldForwardProp: (prop) => prop !== "flipState",
})<{ flipState: FlipState }>(({ flipState }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  transformStyle: "preserve-3d",
  ...(flipState === "idle-front" && { transform: "rotateY(0deg)" }),
  ...(flipState === "idle-back" && { transform: "rotateY(180deg)" }),
  ...(flipState === "flipping-to-back" && {
    animation: `${flipForward} 0.84s ease-in-out forwards`,
  }),
  ...(flipState === "flipping-to-front" && {
    animation: `${flipBackward} 0.84s ease-in-out forwards`,
  }),
}));

const CenterFace = styled(Box, {
  label: "CenterFace",
  shouldForwardProp: (prop) => prop !== "isBack",
})<{ isBack?: boolean }>(({ isBack }) => ({
  position: "absolute",
  width: "100%",
  height: "100%",
  backfaceVisibility: "hidden",
  ...(isBack && { transform: "rotateY(180deg)" }),
}));

const ColorOverlay = styled(Box, {
  label: "MSHatDisplayColorOverlay",
  shouldForwardProp: (prop) => prop !== "overlayColor" && prop !== "visible",
})<{ overlayColor?: string; visible?: boolean }>(
  ({ overlayColor, visible }) => ({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: overlayColor || "transparent",
    opacity: visible ? 0.4 : 0,
    pointerEvents: "none",
    transition: "opacity 0.84s ease-in-out",
  }),
);

export const MSHatDisplay = ({
  hat,
  hatSentiment,
  onHatSentimentChange,
  heardOf,
  onHeardOfChange,
  onExit,
  onHatClick,
  onPathwayClick,
  onClusterClick,
  headerActions,
  activityCompletions = {},
  perkQuirkSentiments = [],
  onPerkQuirkSentimentChange,
  onActivityComplete,
  onXPEarned,
  selectedRegionSalary,
  nationalSalary,
  cmsFacets = [],
  endorsedSkillIds,
  userHighInterestRiasec,
  userTopWorkValues,
}: MSHatDisplayProps) => {
  const [activeActivity, setActiveActivity] = useState<ActivityType | null>(
    null,
  );
  const [flipState, setFlipState] = useState<FlipState>("idle-front");

  const isFlipped =
    flipState === "idle-back" || flipState === "flipping-to-back";

  const handleFlip = () => {
    setFlipState((prev) =>
      prev === "idle-front" || prev === "flipping-to-front"
        ? "flipping-to-back"
        : "flipping-to-front",
    );
  };

  const handleFlipAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    setFlipState((prev) =>
      prev === "flipping-to-back" ? "idle-back" : "idle-front",
    );
  };

  const clusterColor = hat.msCareerCluster?.[0]?.colorTheme;

  const handleActivityClick = (activityType: ActivityType) => {
    setActiveActivity(activityType);
  };

  const handleActivityClose = () => {
    setActiveActivity(null);
  };

  // Render active activity full-screen
  if (activeActivity === "perksquirks") {
    return (
      <PerkQuirkActivity
        hat={hat}
        onClose={handleActivityClose}
        perkQuirkSentiments={perkQuirkSentiments}
        onSentimentChange={onPerkQuirkSentimentChange}
        activityCompletion={
          activityCompletions[`act-perksquirks-${hat.id}`] || null
        }
        onComplete={() =>
          onActivityComplete?.("perksquirks", `perksquirks-${hat.id}`)
        }
        onXPEarned={onXPEarned}
      />
    );
  }

  if (activeActivity === "jobgear") {
    return (
      <JobGearActivity
        hat={hat}
        onClose={handleActivityClose}
        activityCompletion={
          activityCompletions[`act-jobgear-${hat.id}`] || null
        }
        onComplete={() => onActivityComplete?.("jobgear", `jobgear-${hat.id}`)}
        onXPEarned={onXPEarned}
      />
    );
  }

  if (activeActivity === "highschoolpriorities") {
    return (
      <HighSchoolPrioritiesActivity
        hat={hat}
        onClose={handleActivityClose}
        activityCompletion={
          activityCompletions[`act-highschoolpriorities-${hat.id}`] || null
        }
        onComplete={() =>
          onActivityComplete?.(
            "highschoolpriorities",
            `highschoolpriorities-${hat.id}`,
          )
        }
        onXPEarned={onXPEarned}
      />
    );
  }

  if (activeActivity === "afterhighschool") {
    return (
      <AfterHighSchoolActivity
        hat={hat}
        onClose={handleActivityClose}
        activityCompletion={
          activityCompletions[`act-afterhighschool-${hat.id}`] || null
        }
        onComplete={() =>
          onActivityComplete?.("afterhighschool", `afterhighschool-${hat.id}`)
        }
        onXPEarned={onXPEarned}
      />
    );
  }

  return (
    <DisplayContainer>
      <BackgroundImage backgroundUrl={hat.backgroundImage?.url} />
      <ColorOverlay overlayColor={clusterColor} visible={isFlipped} />

      <Header onExit={onExit} headerActions={headerActions} />

      <MainContent>
        <MainContainer>
          <SidePanelWrapper>
            <SideFace visible={!isFlipped}>
              <LeftPanel
                hat={hat}
                onHatClick={onHatClick}
                onPathwayClick={onPathwayClick}
                onClusterClick={onClusterClick}
                demandLabel={selectedRegionSalary?.demand}
                demandRegionName={selectedRegionSalary?.regionName}
              />
            </SideFace>
            <SideFace visible={isFlipped}>
              <BackLeftPanel hat={hat} endorsedSkillIds={endorsedSkillIds} />
            </SideFace>
          </SidePanelWrapper>

          <CenterFlipWrapper>
            <CenterFlipper
              flipState={flipState}
              onAnimationEnd={handleFlipAnimationEnd}
            >
              <CenterFace>
                <CenterPanel
                  hat={hat}
                  heardOf={heardOf}
                  onHeardOfChange={onHeardOfChange}
                  onFlip={handleFlip}
                  clusterColor={clusterColor}
                  hatSentiment={hatSentiment}
                  onHatSentimentChange={onHatSentimentChange}
                />
              </CenterFace>
              <CenterFace isBack>
                <BackCenterPanel
                  hat={hat}
                  onFlip={handleFlip}
                  cmsFacets={cmsFacets}
                  userHighInterestRiasec={userHighInterestRiasec}
                  userTopWorkValues={userTopWorkValues}
                />
              </CenterFace>
            </CenterFlipper>
          </CenterFlipWrapper>

          <SidePanelWrapper>
            <SideFace visible={!isFlipped}>
              <ActivityPanel
                activityCompletions={activityCompletions}
                onActivityClick={handleActivityClick}
                hatId={hat.id}
              />
            </SideFace>
            <SideFace visible={isFlipped}>
              <BackRightPanel
                hat={hat}
                selectedRegionSalary={selectedRegionSalary}
                nationalSalary={nationalSalary}
              />
            </SideFace>
          </SidePanelWrapper>
        </MainContainer>
      </MainContent>
    </DisplayContainer>
  );
};
